import React, { useCallback, useState } from 'react';
import CloseIcon from '@material-ui/icons/Close';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import SendIcon from '@material-ui/icons/Send';
import { MultipleLineSelect } from 'features/message-center/components';
import { useDraftTextMessage } from 'features/message-center/hooks';
import { useChatContext } from 'features/message-center/providers';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import strings from 'strings';
import { FPIconButton, When } from 'UI/components/atoms';
import { FeatureFlags } from 'UI/constants/featureFlags';

import { AttachmentUploader, MessageTemplates } from './components';
import {
  ActionButtonContainer,
  AttachmentChip,
  CharCounterTypography,
  ComposerContainer,
  ExceededTypography,
  FlexBetweenRow,
  MessageInput,
  MessageInputWrapper,
  SendActionButtonContainer
} from './MessageComposer.styled';

const MAX_CHARS_ENV =
  window.GPAC_ENV?.TEXT_MESSAGE_MAX_CHARS ?? process.env.REACT_APP_TEXT_MESSAGE_MAX_CHARS;
const MAX_CHARS = MAX_CHARS_ENV ? Number(MAX_CHARS_ENV) : undefined;

const ATTACHMENT_STRING = strings.messageCenter.attachments.attachment;

export const MessageComposer = ({
  id,
  disabled,
  entityId,
  entityType,
  externalNumber,
  maxChars = MAX_CHARS,
  onClickSend,
  onMessageChange,
  recipientFullName,
  showSelectMultipleLine
}) => {
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();
  const showTemplates = checkIfFeatureFlagEnabled(FeatureFlags.MessageCenterTemplates);
  const [message, setMessage] = useState('');
  const [attachment, setAttachment] = useState(null);

  const { recipient, recipientPhoneWithCode } = useChatContext();
  const recipientPhone = externalNumber ?? recipientPhoneWithCode;
  const contact = {
    entityId: entityId ?? recipient?.entityId,
    entityType: entityType ?? recipient?.entityType,
    fullName: recipientFullName ?? recipient?.name
  };
  const hasExceededMaxSize = maxChars !== undefined ? message.length > maxChars : false;

  const handleSetDraftMessage = useCallback((draftMessage, draftAttachment) => {
    setMessage(draftMessage);
    setAttachment(draftAttachment);
  }, []);

  const { saveDraftMessage, saveDraftAttachment, removeDraftMessage, removeDraftAttachment } =
    useDraftTextMessage({
      id,
      onChangeMessage: handleSetDraftMessage
    });

  const handleSelectTemplate = newText => {
    setMessage(newText);
  };

  const handleUploadAttachment = (_name, value) => {
    setAttachment(value);
    saveDraftAttachment(value);
    onMessageChange && onMessageChange({ draftAttachment: value || ATTACHMENT_STRING });
  };

  const handleChangeText = (name, text) => {
    setMessage(text);
    saveDraftMessage(text);
    onMessageChange && onMessageChange({ draftMessage: text });
  };

  const handleDeleteAttachment = () => {
    setAttachment(null);
    removeDraftAttachment();
    onMessageChange && onMessageChange({ draftAttachment: null });
  };

  const handleClickSend = () => {
    if (hasExceededMaxSize) return;

    const finalMessage = message.trim();
    if (!finalMessage && !attachment) return;

    onClickSend({
      message: finalMessage,
      attachment: { url: attachment?.url, size: attachment?.size_in_bytes },
      to: recipientPhone,
      contact
    });
    setMessage('');
    setAttachment(null);
    onMessageChange && onMessageChange({ draftMessage: '', draftAttachment: null });
    removeDraftMessage();
    removeDraftAttachment();
  };

  const handleKeyDown = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleClickSend();
    }
  };

  return (
    <ComposerContainer>
      <FlexBetweenRow>
        <When condition={showSelectMultipleLine}>
          <MultipleLineSelect />
        </When>
        <When condition={attachment}>
          <AttachmentChip
            icon={<InsertDriveFileIcon />}
            label={attachment?.file_name}
            onDelete={handleDeleteAttachment}
            color="secondary"
            clickable={false}
            deleteIcon={<CloseIcon />}
          />
        </When>
      </FlexBetweenRow>

      <FlexBetweenRow>
        <ActionButtonContainer>
          <AttachmentUploader disabled={disabled} onClick={handleUploadAttachment} />

          <When condition={showTemplates}>
            <MessageTemplates disabled={disabled} onSelectTemplate={handleSelectTemplate} />
          </When>
        </ActionButtonContainer>

        <MessageInputWrapper>
          <MessageInput
            disabled={disabled}
            fullWidth
            multiline
            onChange={handleChangeText}
            onKeyDown={handleKeyDown}
            placeholder={strings.messageCenter.messageComposer.placeholder}
            minRows={1}
            maxRows={5}
            value={message}
            error={hasExceededMaxSize}
          />
          <When condition={Boolean(maxChars)}>
            <CharCounterTypography variant="caption" color="textSecondary">
              {message.length}/{maxChars}
            </CharCounterTypography>
          </When>

          <When condition={hasExceededMaxSize}>
            <ExceededTypography variant="caption" color="error">
              {strings.formatString(strings.messageCenter.messageComposer.exceeded, {
                limit: maxChars
              })}
            </ExceededTypography>
          </When>
        </MessageInputWrapper>

        <SendActionButtonContainer>
          <FPIconButton
            aria-label={strings.messageCenter.messageComposer.sendMessageLabel}
            onClick={handleClickSend}
            disabled={hasExceededMaxSize}
            tooltipProps={{
              title: strings.messageCenter.messageComposer.tooltip
            }}
          >
            <SendIcon color={hasExceededMaxSize ? 'disabled' : 'primary'} />
          </FPIconButton>
        </SendActionButtonContainer>
      </FlexBetweenRow>
    </ComposerContainer>
  );
};
